import { useModal } from '@ebay/nice-modal-react'
import { Link } from '@tanstack/router'
import { useEffect, useState } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import useKycInfo from '../../hooks/useKycInfo'
import { useBoundStore } from '../../store/store'
import RequestPasswordResetModal from '../common/request-password-reset-modal'
import ToastMessage from '../common/toast-message'
import Icon from '../icon'
import UnsuccessfulVerificationModal from '../kyc/unsuccessful-verification-modal'

type MyProfileDrawerTabsProps = {
  closeProfileDrawer: () => void
  verif_request_count: number
}

const MyProfileDrawerTabs = ({
  closeProfileDrawer,
  verif_request_count,
}: MyProfileDrawerTabsProps) => {
  const { isVerifiedBadge, getKycInfo } = useKycInfo()
  const oauth = useBoundStore(state => state.user.oauth)
  const { t } = useTranslation('drawer')
  const [isOpen, setIsOpen] = useState(false)
  const modal = useModal()
  const toastMessage = useModal(ToastMessage)
  const unsuccessfulVerificationModal = useModal(UnsuccessfulVerificationModal)
  const requestPasswordResetModal = useModal(RequestPasswordResetModal)
  const categories = useMemo(
    () => [
      {
        label: t('personal-data'),
        value: 0,
      },
      {
        label: t('verify-user'),
        value: 1,
      },
      ...(oauth
        ? []
        : [
            {
              label: t('change-password'),
              value: 2,
            },
          ]),
    ],
    [],
  )

  useEffect(() => {
    if (!modal.visible) return
    getKycInfo()
  }, [modal.visible])

  const kycBadge = useMemo(() => {
    if (isVerifiedBadge === 0) {
      return (
        <p
          className="text-white text-[9px] font-normal bg-[#58d66d] leading-6 px-[7px] py-0 rounded-xl"
          style={{ backgroundColor: '#515151', color: '#fff' }}>
          {t('verify', { ns: 'kyc' })}
        </p>
      )
    } else if (isVerifiedBadge === 1) {
      return (
        <p
          className="text-white text-[9px] font-normal bg-[#58d66d] leading-6 px-[7px] py-0 rounded-xl"
          style={{ backgroundColor: '#58D66D' }}>
          {t('verified', { ns: 'kyc' })}
        </p>
      )
    } else if (isVerifiedBadge === 2) {
      return (
        <p
          className="text-white text-[9px] font-normal bg-[#58d66d] leading-6 px-[7px] py-0 rounded-xl"
          style={{ backgroundColor: '#E9BF2E' }}>
          {t('in-review', { ns: 'kyc' })}
        </p>
      )
    } else if (isVerifiedBadge === 3) {
      return (
        <p
          className="text-white text-[9px] font-normal bg-[#58d66d] leading-6 px-[7px] py-0 rounded-xl"
          style={{ backgroundColor: '#E74242' }}>
          {t('declined', { ns: 'kyc' })}
        </p>
      )
    }

    return null
  }, [isVerifiedBadge])

  return (
    <div
      className="sidebar-theme transition-all duration-[0.2s] ease-[ease-in-out] overflow-hidden"
      style={{
        height: isOpen ? categories.length * 35 + 51 : '39px',
      }}>
      <div
        className="flx-btw-ctr px-5 py-2.5"
        onClick={() => setIsOpen(prev => (prev ? false : true))}>
        <div className="flx-str-ctr">
          <p className="text-highlight mx-2.5">{t('my-profile')}</p>
        </div>
        <Icon
          className="transition-all duration-[0.2s] ease-[ease-in-out]"
          id="triangle-down"
          width="16"
          height="10"
          fill="#46aef7"
          style={{ transform: `rotate(${isOpen ? '-180' : '0'}deg)` }}
        />
      </div>
      <div className="bg-secondary pl-5 pr-0 py-1.5">
        {categories.map(category => (
          <Link to="/" key={category.value}>
            <div
              className="leading-[35px] last:border-0"
              onClick={closeProfileDrawer}>
              {category.value === 0 ? (
                <Link to="/profile">
                  <p className="text-highlight mx-2.5">{category.label}</p>
                </Link>
              ) : category.value === 1 ? (
                <Link
                  to={
                    [1, 3].includes(isVerifiedBadge) ||
                    (verif_request_count > 3 && +isVerifiedBadge === 3)
                      ? undefined
                      : '/profile/kyc'
                  }>
                  <p
                    className="flx-btw-ctr text-highlight mx-2.5"
                    onClick={() => {
                      if (+isVerifiedBadge === 1) return

                      if (verif_request_count >= 3 && +isVerifiedBadge === 3) {
                        toastMessage.show({
                          text: t('contact-customer-service', { ns: 'kyc' }),
                        })
                        return
                      }
                      if (isVerifiedBadge === 3) {
                        unsuccessfulVerificationModal.show()
                        closeProfileDrawer()
                        return
                      }

                      closeProfileDrawer()
                    }}>
                    {t('verify-user')}
                    {kycBadge}
                  </p>
                </Link>
              ) : (
                <p
                  className="text-highlight mx-2.5"
                  onClick={() => {
                    requestPasswordResetModal.show()
                  }}>
                  {category.label}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}

export default MyProfileDrawerTabs
